import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dumbbell, Apple, TrendingUp, Activity, Swords, Users, Timer, KeyRound, AlertCircle, Flame, Zap, Target, Shield } from 'lucide-react';
import axios from 'axios';
import { API, getLocalDateString } from '../App';
import HamburgerMenu from '../components/HamburgerMenu';
import ProductRecommendation from '../components/ProductRecommendation';
import { getRecommendations } from '../data/armoryProducts';
import { getWorkoutTypeLabel } from '../utils/workoutTypes';

const MainHub = ({ user, onLogout }) => {
  const navigate = useNavigate();

  const selectedWorkoutType =
    localStorage.getItem('workoutType') || null;

  const handleQuickWorkout = () => {
    if (!selectedWorkoutType) {
      navigate('/workout-selection');
      return;
    }

    navigate('/log-workout', {
      state: {
        quickWorkout: true,
      },
    });
  };
  const [stats, setStats] = useState({
    recentWorkouts: 0,
    currentWeight: null,
    todayCalories: 0
  });
  const [pendingResets, setPendingResets] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [streakData, setStreakData] = useState(null);
  const [caloriesBurnedToday, setCaloriesBurnedToday] = useState(0);

  const loadStats = async () => {
    try {
      // Check if user is admin
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const adminStatus = storedUser.is_admin || false;
      setIsAdmin(adminStatus);
      
      // If admin, check for pending password reset requests
      if (adminStatus) {
        try {
          const token = localStorage.getItem('token');
          const resetResponse = await axios.get(`${API}/admin/password-reset-requests`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPendingResets(resetResponse.data.length);
        } catch (err) {
          // Endpoint might not exist or not admin
        }
      }
      
      // Get recent workout count
      const workoutsResponse = await axios.get(`${API}/workouts/history`);
      const recentWorkouts = workoutsResponse.data.history?.length || 0;

      // Nutrition is premium-only. Avoid a deliberate 403 probe for known free users.
      if (user?.tier === 'premium') {
        const today = getLocalDateString();
        try {
          const nutritionResponse = await axios.get(`${API}/nutrition/daily/${today}`);
          const todayCalories = nutritionResponse.data.totals?.calories || 0;
          setStats(prev => ({ ...prev, todayCalories }));
        } catch (err) {
          // Premium status may have expired; nutrition page performs the authoritative check.
        }
      }

      // Get weight stats - using weight/logs endpoint since weight/stats doesn't exist
      try {
        const weightResponse = await axios.get(`${API}/weight/logs?limit=1`);
        const currentWeight = weightResponse.data[0]?.weight || null;
        setStats(prev => ({ ...prev, currentWeight }));
      } catch (err) {
        // Weight tracking might not be available
      }

      setStats(prev => ({ ...prev, recentWorkouts }));
    } catch (error) {
      console.error('Error loading stats:', error);
    }

    // Load streaks
    try {
      const streakResponse = await axios.get(`${API}/progress/streaks`);
      setStreakData(streakResponse.data);
    } catch (err) {
      console.error('Error loading streaks:', err);
    }

    // Load today's calories burned (workouts + activities + yoga)
    try {
      const burnedRes = await axios.get(`${API}/progress/calories-burned`);
      setCaloriesBurnedToday(burnedRes.data.daily || 0);
    } catch (err) {
      // not critical
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900 pb-20">
      <HamburgerMenu />
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Admin Notification Banner */}
        {isAdmin && pendingResets > 0 && (
          <div 
            onClick={() => navigate('/admin')}
            className="mb-4 p-3 bg-gradient-to-r from-blood-900/50 to-blood-800/30 border border-blood-700 rounded-lg cursor-pointer hover:border-blood-500 transition-colors"
            data-testid="admin-notification-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blood-700 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">
                  {pendingResets} Password Reset Request{pendingResets > 1 ? 's' : ''} Pending
                </p>
                <p className="text-stone-400 text-xs">Tap to review in Admin Panel</p>
              </div>
              <AlertCircle className="w-5 h-5 text-blood-400" />
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 tracking-wider" style={{fontFamily: 'Georgia, serif'}}>
            Welcome back!
          </h1>
          <p className="text-stone-500 uppercase text-xs tracking-[0.25em]">What are you training today?</p>
        </div>

        {/* Streak Bar */}
        {streakData && (
          <div className="flex items-center gap-4 mb-6 glass border border-stone-800 rounded-lg px-4 py-3" data-testid="hub-streak-bar">
            <div className="flex items-center gap-2">
              <Flame className={`w-6 h-6 ${streakData.current_streak > 0 ? 'text-orange-400 streak-fire' : 'text-stone-600'}`} />
              <div>
                <span className={`text-lg font-bold ${streakData.current_streak > 0 ? 'text-orange-400' : 'text-stone-500'}`}>{streakData.current_streak}</span>
                <span className="text-stone-500 text-xs ml-1">day streak</span>
              </div>
            </div>
            <div className="h-6 w-px bg-stone-700" />
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-blood-400" />
              <span className="text-stone-300 text-sm">{streakData.this_week}/{streakData.weekly_goal} this week</span>
            </div>
            <div className="h-6 w-px bg-stone-700 hidden sm:block" />
            <div className="hidden sm:flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              <span className="text-stone-300 text-sm">Best: {streakData.longest_streak}</span>
            </div>
            <div className="h-6 w-px bg-stone-700 hidden md:block" />
            <div className="hidden md:flex items-center gap-2" data-testid="hub-calories-burned-today">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="text-stone-300 text-sm">{caloriesBurnedToday.toLocaleString()} cal burned today</span>
            </div>
          </div>
        )}

        {/* Main Action Tiles - Mobile-optimized grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          {/* Workout Tile */}
          <Card 
            className="glass tile-hover p-4 md:p-6 cursor-pointer border border-blood-900/50 hover:border-blood-600 active:scale-[0.98]"
            onClick={() => navigate('/workout-selection')}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-blood-900 to-blood-950 border border-blood-800 flex items-center justify-center mb-2">
                <Dumbbell className="w-6 h-6 md:w-8 md:h-8 text-blood-400" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-white tracking-wider">WORKOUT</h2>
              <p className="text-stone-500 text-xs">Forge strength</p>
            </div>
          </Card>

          {/* Quick Workout Tile */}
          <Card
            className="glass tile-hover p-4 md:p-6 cursor-pointer border border-amber-800/60 hover:border-amber-500 active:scale-[0.98]"
            onClick={handleQuickWorkout}
            data-testid="quick-workout-tile"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-amber-900/70 to-stone-950 border border-amber-700 flex items-center justify-center mb-2">
                <Zap className="w-6 h-6 md:w-8 md:h-8 text-amber-400" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-white tracking-wider">
                QUICK WORKOUT
              </h2>
              <p className="text-stone-500 text-xs">
                {selectedWorkoutType
                  ? `Start today's ${getWorkoutTypeLabel(selectedWorkoutType)} plan`
                  : 'Choose your training environment'}
              </p>
            </div>
          </Card>

          {/* Nutrition Tile */}
          <Card 
            className="glass tile-hover p-4 md:p-6 cursor-pointer border border-stone-700/50 hover:border-blood-700 active:scale-[0.98]"
            onClick={() => navigate('/nutrition')}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-stone-800 to-stone-950 border border-stone-700 flex items-center justify-center mb-2">
                <Apple className="w-6 h-6 md:w-8 md:h-8 text-stone-400" />
              </div>
              <div className="flex items-center gap-2">
                <h2 className="text-base md:text-lg font-bold text-white tracking-wider">NUTRITION</h2>
                {user?.tier !== 'premium' && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-amber-300 border border-amber-800 bg-amber-950/40 px-1.5 py-0.5 rounded">
                    Premium
                  </span>
                )}
              </div>
              <p className="text-stone-500 text-xs">Daily fuel</p>
            </div>
          </Card>

          {/* Active Recovery Tile */}
          <Card 
            className="glass tile-hover p-4 md:p-6 cursor-pointer border border-blood-900/50 hover:border-blood-600 active:scale-[0.98]"
            onClick={() => navigate('/active-recovery')}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-blood-900 to-blood-950 border border-blood-800 flex items-center justify-center mb-2">
                <Timer className="w-6 h-6 md:w-8 md:h-8 text-blood-400" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-white tracking-wider">RECOVERY</h2>
              <p className="text-stone-500 text-xs">Yoga & Stretch</p>
            </div>
          </Card>

          {/* Battle Arena Tile */}
          <Card 
            className="glass tile-hover p-4 md:p-6 cursor-pointer border border-blood-900/50 hover:border-blood-600 active:scale-[0.98]"
            onClick={() => navigate('/battle-arena')}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-blood-900 to-blood-950 border border-blood-800 flex items-center justify-center mb-2">
                <Swords className="w-6 h-6 md:w-8 md:h-8 text-blood-400" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-white tracking-wider">CHALLENGES</h2>
              <p className="text-stone-500 text-xs">Challenge friends</p>
            </div>
          </Card>

          {/* Friends Tile */}
          <Card 
            className="glass tile-hover p-4 md:p-6 cursor-pointer border border-stone-700/50 hover:border-blood-700 active:scale-[0.98]"
            onClick={() => navigate('/friends')}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-stone-800 to-stone-950 border border-stone-700 flex items-center justify-center mb-2">
                <Users className="w-6 h-6 md:w-8 md:h-8 text-stone-400" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-white tracking-wider">FRIENDS</h2>
              <p className="text-stone-500 text-xs">Your squad</p>
            </div>
          </Card>

          {/* Progress Tile */}
          <Card 
            className="glass tile-hover p-4 md:p-6 cursor-pointer border border-blood-900/50 hover:border-blood-600 active:scale-[0.98]"
            onClick={() => navigate('/progress')}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-blood-900 to-blood-950 border border-blood-800 flex items-center justify-center mb-2">
                <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-blood-400" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-white tracking-wider">PROGRESS</h2>
              <p className="text-stone-500 text-xs">Track gains</p>
            </div>
          </Card>

          {/* Armory Tile */}
          <Card 
            className="glass tile-hover p-4 md:p-6 cursor-pointer border border-amber-900/40 hover:border-amber-600 active:scale-[0.98] col-span-2 md:col-span-1"
            onClick={() => navigate('/armory')}
            data-testid="armory-tile"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-amber-900 to-amber-950 border border-amber-800 flex items-center justify-center mb-2">
                <Shield className="w-6 h-6 md:w-8 md:h-8 text-amber-400" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-white tracking-wider">ARMORY</h2>
              <p className="text-stone-500 text-xs">Gear & Supplements</p>
            </div>
          </Card>
        </div>

        {/* Contextual Product Recommendations - only for non-premium */}
        {user?.tier !== 'premium' && (
          <div className="mb-4" data-testid="hub-recommendations">
            <div className="flex items-center justify-between mb-2">
              <p className="text-stone-500 text-xs uppercase tracking-wider">Recommended Gear</p>
              <button onClick={() => navigate('/armory')} className="text-blood-500 text-xs hover:text-blood-400">
                View All →
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {getRecommendations(['post_workout', 'protein', 'home_workout'], 2).map(p => (
                <ProductRecommendation key={p.id} product={p} variant="compact" />
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity Section */}
        <Card 
          className="glass p-4 cursor-pointer hover:scale-[1.01] transition-all border border-stone-700/50 hover:border-stone-600"
          onClick={() => navigate('/activity')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-stone-800 border border-stone-700 flex items-center justify-center">
              <Activity className="w-5 h-5 text-stone-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-white">Recent Activity</h3>
              <p className="text-stone-500 text-xs">
                {stats.recentWorkouts > 0 
                  ? `${stats.recentWorkouts} workouts this month`
                  : 'Start your journey'}
              </p>
            </div>
            <div className="text-stone-600">→</div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MainHub;
